import {useEffect} from "react";
import { centerCrop, makeAspectCrop } from "react-image-crop";

export function useDebounceEffect(fn, waitTime, deps = [])  {
  useEffect(() => {
    const t = setTimeout(() => {
      fn.apply(undefined, [...deps])
    }, waitTime)

    return () => {
      clearTimeout(t)
    }
  }, deps)
}

/**
 * Initialize a centered crop area for an image
 * @param {number} width - Image natural width
 * @param {number} height - Image natural height
 * @param {number} aspectRatio - Aspect ratio for crop (default: 1 for square)
 * @returns {Object} Initial crop object
 */
export function initializeCrop(width, height, aspectRatio = 1) {
  let defaultCrop = { unit: "%" };
  if (width > height) {
    defaultCrop.height = 80;
  } else {
    defaultCrop.width = 80;
  }

  return centerCrop(
    makeAspectCrop(defaultCrop, aspectRatio, width, height),
    width,
    height
  );
}

/**
 * Convert crop coordinates from percentage to pixels
 * @param {Object} crop - Crop object (may be in % or px)
 * @param {number} imageWidth - Display width of image
 * @param {number} imageHeight - Display height of image
 * @returns {Object} Crop object in pixels
 */
export function convertCropToPx(crop, imageWidth, imageHeight) {
  if (crop.unit === "px") {
    return crop;
  }
  
  return {
    ...crop,
    unit: "px",
    width: (crop.width / 100) * imageWidth,
    height: (crop.height / 100) * imageHeight,
    x: (crop.x / 100) * imageWidth,
    y: (crop.y / 100) * imageHeight,
  };
}

/**
 * Create a cropped image blob from an image element and crop area
 * @param {HTMLImageElement} imageElement - The image element to crop
 * @param {Object} crop - Crop object (will be converted to px if needed)
 * @param {number} outputWidth - Output width in pixels (default: 1000)
 * @param {number} outputHeight - Output height in pixels (default: 1000)
 * @param {number} quality - JPEG quality 0-1 (default: 0.9)
 * @returns {Promise<Blob>} Cropped image blob
 */
export async function createCroppedBlob(imageElement, crop, outputWidth = 1000, outputHeight = 1000, quality = 0.9) {
  const scale = imageElement.naturalWidth / imageElement.width;
  
  // Convert crop to px if needed
  let cropPx = convertCropToPx(crop, imageElement.width, imageElement.height);
  
  const offscreen = new OffscreenCanvas(outputWidth, outputHeight);
  const ctx = offscreen.getContext("2d");
  
  if (!ctx) {
    throw new Error('Could not get canvas context');
  }
  
  ctx.drawImage(
    imageElement,
    cropPx.x * scale,
    cropPx.y * scale,
    cropPx.width * scale,
    cropPx.height * scale,
    0,
    0,
    offscreen.width,
    offscreen.height
  );
  
  return await offscreen.convertToBlob({
    type: "image/jpeg",
    quality: quality,
  });
}

const TO_RADIANS = Math.PI / 180;

export async function canvasPreview(image, canvas, crop, scale = 1, rotate = 0) {
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const pixelRatio = window.devicePixelRatio;

  canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
  canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = 'high';

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;

  const rotateRads = rotate * TO_RADIANS;
  const centerX = image.naturalWidth / 2;
  const centerY = image.naturalHeight / 2;

  ctx.save();

  // 5) Move the crop origin to the canvas origin (0,0)
  ctx.translate(-cropX, -cropY);
  // 4) Move the origin to the center of the original position
  ctx.translate(centerX, centerY);
  // 3) Rotate around the origin
  ctx.rotate(rotateRads);
  // 2) Scale the image
  ctx.scale(scale, scale);
  // 1) Move the center of the image to the origin (0,0)
  ctx.translate(-centerX, -centerY);
  ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, 0, 0, image.naturalWidth, image.naturalHeight);

  ctx.restore();
}
